<template>
    <LoadingComponent :props="loading" />
    <div class="px-5">
        <div class="w-full max-w-3xl mx-auto rounded-2xl flex overflow-hidden gap-y-6 bg-white border shadow-card mt-24 mb-24 !sm:mb-0">
            <img :src="APP_URL + '/images/required/auth.jpg'" alt="banners"
                class="w-full hidden sm:block sm:max-w-xs md:max-w-sm flex-shrink-0">
            
            <!-- AUTO LOGIN CARD -->
            <form v-if="isAutoLoginMode && !showManualLogin" class="w-full p-6 flex flex-col items-center justify-center text-center my-auto min-h-[350px]" @submit.prevent="login">
                <div class="mb-6 max-w-md">
                    <h3 class="capitalize text-2xl mb-3 font-bold text-primary">{{ $t('label.auto_login') }}</h3>
                    <p class="font-medium text-text leading-relaxed">{{ $t('label.auto_login_description') }}</p>
                    <div v-if="errors.validation"
                        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mt-4 rounded relative" role="alert">
                        <span class="block sm:inline">{{ errors.validation }}</span>
                        <span class="absolute top-0 bottom-0 right-0 px-4 py-3 cursor-pointer" @click="close">
                            <i class="lab lab-close-circle-line margin-top-5-px"></i>
                        </span>
                    </div>
                </div>

                <!-- Hidden credential fields -->
                <input type="hidden" v-model="form.email" id="autoEmail">
                <input type="hidden" v-model="form.password" id="autoPassword">

                <!-- reCAPTCHA widget container -->
                <div v-if="isRecaptchaRequired" class="mb-4 flex flex-col items-center">
                    <div id="recaptcha-auto-container"></div>
                    <small class="db-field-alert mt-1" v-if="errors.recaptcha">{{ errors.recaptcha[0] }}</small>
                </div>

                <div class="flex flex-col items-center gap-4">
                    <button type="submit"
                        :disabled="isLoginPending"
                        class="font-bold text-center px-8 h-12 min-w-[180px] rounded-full bg-primary text-white capitalize shadow-md hover:bg-primary/90 transition-all disabled:opacity-80 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                        <template v-if="isLoginPending">
                            <span class="btn-spinner"></span>
                            <span>{{ $t('label.processing') }}</span>
                        </template>
                        <template v-else>
                            {{ $t('label.sign_in') }}
                        </template>
                    </button>

                    <button type="button" @click="showManualLogin = true"
                        :disabled="isLoginPending"
                        class="text-sm font-medium text-gray-500 hover:text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none">
                        {{ $t('label.use_manual_login') }}
                    </button>
                </div>
            </form>

            <!-- STANDARD LOGIN FORM -->
            <form v-else class="w-full p-6" @submit.prevent="login">
                <div class="text-center mb-8">
                    <h3 class="capitalize text-2xl mb-2 font-bold text-primary">{{ $t('label.sign_in') }}</h3>
                    <p class="font-medium">{{ $t('message.login_to_access_account') }}</p>
                    <div v-if="errors.validation"
                        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mb-5 rounded relative" role="alert">
                        <span class="block sm:inline">{{ errors.validation }}</span>
                        <span class="absolute top-0 bottom-0 right-0 px-4 py-3 cursor-pointer" @click="close">
                            <i class="lab lab-close-circle-line margin-top-5-px"></i>
                        </span>
                    </div>
                </div>

                <div :class="!toggleValue ? 'mb-6' : ''">
                    <div class="flex items-center justify-between">
                        <label :for="!toggleValue ? 'formEmail' : 'phone'"
                            class="text-sm font-medium capitalize mb-1 field-title required">
                            {{ inputLabel }}
                        </label>
                        <button type="button" class="text-sm font-medium mb-1 text-primary"
                            @click="handleField()">
                            {{ inputButton }}
                        </button>
                    </div>
                    <div v-if="toggleValue" :class="errors.phone ? 'invalid' : ''"
                        class="flex items-center gap-1.5 px-4 h-12 rounded-lg border border-[#D9DBE9] hover:border-primary/30 focus-within:border-primary/30 transition-all duration-500">
                        <div class="w-fit flex-shrink-0 dropdown-group">
                            <button type="button" class="flex items-center gap-1 dropdown-btn">
                                {{ flag }}
                                <span class="whitespace-nowrap flex-shrink-0 text-xs">{{
                                    form.country_code
                                    }}</span>
                                <i class="fa-solid fa-caret-down text-xs"></i>
                            </button>
                            <ul
                                class="p-1.5 w-24 rounded-lg shadow-xl absolute top-8 -left-4 z-10 border border-gray-200 bg-white scale-y-0 origin-top dropdown-list !h-52 !overflow-x-hidden !overflow-y-auto thin-scrolling">
                                <li v-for="countryCode in countryCodes" @click="countryCodeChange(countryCode)"
                                    class="flex items-center gap-2 p-1.5 rounded-md cursor-pointer hover:bg-gray-100">
                                    {{ countryCode.flag_emoji }}
                                    <span class="whitespace-nowrap text-xs">{{ countryCode.calling_code }}</span>
                                </li>
                            </ul>

                        </div>
                        <input v-model="form.phone" v-on:keypress="phoneNumber($event)" v-bind:class="errors.phone
                            ? 'invalid' : ''" type="text" id="phone" class="pl-2 text-sm w-full h-full" />
                    </div>
                    <input v-if="!toggleValue" v-model="form.email" :class="errors.email ? 'invalid' : ''" id="formEmail"
                        type="text"
                        class="w-full h-12 px-4 rounded-lg text-base border border-[#D9DBE9] hover:border-primary/30 focus-within:border-primary/30 transition-all duration-500" />
                    <small class="db-field-alert" v-if="errors.email_or_phone">{{ errors.email_or_phone }}</small>
                    <span v-else>
                        <small class="db-field-alert" v-if="errors.phone && toggleValue">{{ errors.phone[0] }}</small>
                        <small class="db-field-alert" v-if="errors.email && !toggleValue">{{ errors.email[0] }}</small>
                    </span>
                </div>

                <div class="mb-3">
                    <label for="formPassword" class="text-sm font-medium capitalize mb-1 field-title required">
                        {{ $t('label.password') }}
                    </label>
                    <input v-model="form.password" :class="errors.password ? 'invalid' : ''" id="formPassword"
                        type="password"
                        class="w-full h-12 px-4 rounded-lg text-base border border-[#D9DBE9] hover:border-primary/30 focus-within:border-primary/30 transition-all duration-500" />
                    <small class="db-field-alert" v-if="errors.password">{{ errors.password[0] }}</small>
                </div>
                <div class="flex items-center justify-between mb-6">
                    <div class="flex items-center gap-2">
                        <input type="checkbox" id="formRemember" class="custom-checkbox">
                        <label for="formRemember" class="text-sm -mb-0.5 capitalize cursor-pointer whitespace-nowrap">{{
                            $t('label.remember_me')
                            }}</label>
                    </div>
                    <router-link :to="{ name: 'auth.forgotPassword' }" class="text-sm font-medium text-primary">
                        {{ $t('label.forgot_password') }}
                    </router-link>
                </div>

                <!-- reCAPTCHA widget container -->
                <div v-if="isRecaptchaRequired" class="mb-4 flex flex-col items-center">
                    <div id="recaptcha-manual-container"></div>
                    <small class="db-field-alert mt-1" v-if="errors.recaptcha">{{ errors.recaptcha[0] }}</small>
                </div>

                <button type="submit"
                    :disabled="loading.isActive"
                    class="font-bold text-center w-full h-12 rounded-full bg-primary text-white capitalize mb-4 disabled:opacity-80 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    <template v-if="loading.isActive">
                        <span class="btn-spinner"></span>
                        <span>{{ $t('label.processing') }}</span>
                    </template>
                    <template v-else>
                        {{ $t('label.sign_in') }}
                    </template>
                </button>

                <div v-if="isAutoLoginMode" class="text-center mb-4">
                    <button type="button" @click="showManualLogin = false"
                        class="text-sm font-medium text-primary">
                        {{ $t('label.back_to_auto_login') }}
                    </button>
                </div>

                <div class="flex items-center justify-center text-sm gap-1.5">
                    <span class="font-normal text-text">{{ $t('message.dont_have_account') }}</span>
                    <router-link class="capitalize font-medium text-primary" :to="{ name: 'auth.signup' }">
                        {{ $t('label.sign_up') }}
                    </router-link>
                </div>

                <!-- SSO Social Login Buttons -->
                <div v-if="setting && setting.social_login_status == 1 && (setting.google_login_status == 1 || setting.facebook_login_status == 1)" class="mt-6">
                    <div class="relative flex py-2 items-center">
                        <div class="flex-grow border-t border-gray-200"></div>
                        <span class="flex-shrink mx-4 text-xs font-semibold text-gray-400 uppercase">{{ $t('label.or_continue_with') }}</span>
                        <div class="flex-grow border-t border-gray-200"></div>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                        <a v-if="setting.google_login_status == 1" :href="APP_URL + '/api/auth/social/google/redirect'"
                           class="flex items-center justify-center gap-2 border border-gray-300 rounded-full py-2.5 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm">
                            <svg class="w-4 h-4" viewBox="0 0 24 24">
                                <path fill="#EA4335" d="M12 5c1.6 0 3 .6 4.1 1.6l3.1-3.1C17.3 1.7 14.8 1 12 1 7.5 1 3.7 3.6 1.9 7.3l3.7 2.9C6.5 7.3 9 5 12 5z"/>
                                <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.1 2.8-2.4 3.7l3.7 2.9c2.2-2 3.7-5 3.7-8.8z"/>
                                <path fill="#FBBC05" d="M5.6 14.8c-.2-.7-.4-1.5-.4-2.3s.2-1.6.4-2.3L1.9 7.3C.7 9.7 0 12.3 0 15s.7 5.3 1.9 7.7l3.7-2.9c-.8-.7-1.4-1.7-1.8-2.8z"/>
                                <path fill="#34A853" d="M12 23c3.2 0 6-1.1 8-3l-3.7-2.9c-1.1.7-2.5 1.2-4.3 1.2-3 0-5.5-2.3-6.4-5.2L1.9 16C3.7 19.7 7.5 23 12 23z"/>
                            </svg>
                            <span>Google</span>
                        </a>

                        <a v-if="setting.facebook_login_status == 1" :href="APP_URL + '/api/auth/social/facebook/redirect'"
                           class="flex items-center justify-center gap-2 border border-gray-300 rounded-full py-2.5 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm">
                            <svg class="w-4 h-4 fill-[#1877F2]" viewBox="0 0 24 24">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                            <span>Facebook</span>
                        </a>
                    </div>
                </div>

                <!-- Credential Demo Buttons -->
                <div v-if="isAutoLoginMode" class="mt-6 border-t pt-4">
                    <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 text-center">{{ $t('message.for_quick_demo') }}</p>
                    <nav class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <button type="button" @click.prevent="setupCredit('admin')"
                            class="click-to-prop w-full h-10 leading-10 rounded-lg text-center text-sm capitalize text-white bg-orange-500"
                            id="adminClick">
                            {{ $t('label.admin') }}
                        </button>
                        <button type="button" @click.prevent="setupCredit('customer')"
                            class="click-to-prop w-full h-10 leading-10 rounded-lg text-center text-sm capitalize text-white bg-emerald-500"
                            id="customerClick">
                            {{ $t('label.customer') }}
                        </button>
                        <button type="button" @click.prevent="setupCredit('manager')"
                            class="click-to-prop w-full h-10 leading-10 rounded-lg text-center text-sm capitalize text-white bg-sky-600"
                            id="branchManagerClick">
                            {{ $t('label.manager') }}
                        </button>
                        <button type="button" @click.prevent="setupCredit('posOperator')"
                            class="click-to-prop w-full h-10 leading-10 rounded-lg text-center text-sm capitalize text-white bg-purple-500"
                            id="posOperatorClick">
                            {{ $t('label.pos_operator') }}
                        </button>
                    </nav>
                </div>
            </form>
        </div>
    </div>

    <!-- GIVEAWAY MODAL -->
    <Teleport to="body">
        <div v-if="isGiveawayModalOpen"
            class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-light/50 backdrop-blur-sm transition-opacity">
            <div class="bg-white rounded-xl shadow-lg w-full max-w-md mx-4 overflow-hidden">

                <!-- FORM STATE -->
                <div v-if="!isGiveawaySuccessState" class="p-8">
                    <div class="text-center mb-6">
                        <div class="text-5xl mb-3">🎁</div>
                        <h2 class="text-2xl font-bold text-gray-900 mb-2">{{ $t('message.demo_giveaway_title') }}</h2>
                        <p class="text-sm text-gray-500 leading-relaxed">{{ $t('message.demo_giveaway_text') }}</p>
                    </div>

                    <div class="flex flex-col gap-2 mb-6">
                        <div>
                            <input v-model="giveawayForm.name" type="text"
                                class="w-full h-11 px-4 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-primary transition"
                                :placeholder="$t('label.name')" />
                        </div>
                        <div>
                            <input v-model="giveawayForm.email" type="email"
                                class="w-full h-11 px-4 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-primary transition"
                                :placeholder="$t('label.email')" />
                        </div>
                        <div>
                            <input v-model="giveawayForm.phone" type="tel"
                                class="w-full h-11 px-4 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-primary transition"
                                :placeholder="$t('label.phone')" />
                        </div>
                    </div>

                    <div class="flex flex-col gap-2">
                        <button type="button"
                            @click="submitGiveawayLead"
                            :disabled="!isGiveawayFormValid || isSubmittingGiveaway"
                            class="h-12 rounded-xl px-10 font-semibold text-white bg-primary hover:bg-primary/90 hover:opacity-80 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                            <span v-if="isSubmittingGiveaway" class="btn-spinner"></span>
                            {{ $t('button.participate_demo') }}
                        </button>
                        <button type="button"
                            @click="skipGiveawayModal"
                            class="h-12 rounded-xl px-10 text-sm font-medium text-gray-500 hover:text-primary transition-colors">
                            {{ $t('button.skip_and_view_demo') }}
                        </button>
                    </div>
                </div>

                <!-- SUCCESS STATE -->
                <div v-else class="p-8 text-center">
                    <div class="text-5xl mb-2">🎉</div>
                    <h2 class="text-2xl font-bold text-gray-900 mb-3">{{ $t('message.demo_giveaway_success_title') }}</h2>
                    <p class="text-sm text-gray-500 leading-relaxed mb-5">{{ $t('message.demo_giveaway_success_text') }}</p>
                    <button type="button"
                        @click="proceedAfterGiveaway"
                        class="h-12 rounded-xl px-10 font-semibold text-white bg-primary hover:bg-primary/90 transition-all">
                        {{ $t('button.continue_to_demo') }}
                    </button>
                </div>

            </div>
        </div>
    </Teleport>

</template>

<script>
import axios from "axios";
import router from "../../../router";
import LoadingComponent from "../components/LoadingComponent";
import alertService from "../../../services/alertService";
import appService from "../../../services/appService";
import ENV from "../../../config/env";
import roleEnum from "../../../enums/modules/roleEnum";

export default {
    name: "LoginComponent",
    components: { LoadingComponent },
    data() {
        return {
            loading: {
                isActive: false,
            },
            form: {
                email: "",
                phone: "",
                country_code: "",
                password: ""
            },
            flag: "",
            errors: {},
            permissions: {},
            firstMenu: null,
            demo: ENV.DEMO,
            APP_URL: ENV.API_URL,
            toggleValue: false,
            inputLabel: this.$t('label.email'),
            inputButton: this.$t('label.use_phone_instead'),
            isAutoLoginMode: (ENV.DEMO === 'true' || ENV.DEMO === true || ENV.DEMO === '1' || ENV.DEMO === 1) && (ENV.AUTO_LOGIN === 'true' || ENV.AUTO_LOGIN === true || ENV.AUTO_LOGIN === '1' || ENV.AUTO_LOGIN === 1) && (ENV.LOGIN_MODE === 'auto'),
            showManualLogin: false,
            recaptchaToken: "",
            giveawayForm: {
                name: "",
                email: "",
                phone: ""
            },
            isGiveawayModalOpen: false,
            isSubmittingGiveaway: false,
            isGiveawaySuccessState: false,
            giveawayHandled: false,
            isLoginPending: false,
        }
    },
    computed: {
        carts: function () {
            return this.$store.getters['frontendCart/lists'];
        },
        countryCodes: function () {
            return this.$store.getters['frontendCountryCode/lists'];
        },
        setting: function () {
            return this.$store.getters['frontendSetting/lists'];
        },
        isDemoGiveawayEnabled: function () {
            const setting = this.setting;
            return (ENV.DEMO_GIVEAWAY_ENABLE === 'true' || ENV.DEMO_GIVEAWAY_ENABLE === true || ENV.DEMO_GIVEAWAY_ENABLE === '1' || ENV.DEMO_GIVEAWAY_ENABLE === 1)
                || (setting && (setting.demo_giveaway_enable === 'true' || setting.demo_giveaway_enable === 1 || setting.demo_giveaway_enable === true));
        },
        spinnerColor: function () {
            return this.setting?.theme_color || "#FD8B0E";
        },
        isGiveawayFormValid: function () {
            const hasName = Boolean(this.giveawayForm.name && this.giveawayForm.name.trim().length > 0);
            const hasContact = Boolean(
                (this.giveawayForm.email && this.giveawayForm.email.trim().length > 0) ||
                (this.giveawayForm.phone && this.giveawayForm.phone.trim().length > 0)
            );
            return hasName && hasContact;
        },
        isRecaptchaRequired: function () {
            const setting = this.setting;
            if (!setting || Number(setting.recaptcha_status) !== 1 || !setting.recaptcha_site_key) {
                return false;
            }
            if (this.isAutoLoginMode && !this.showManualLogin) {
                return true;
            }
            return Number(setting.recaptcha_login_enabled) === 1;
        }
    },
    watch: {
        isRecaptchaRequired(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.loadRecaptchaScript();
                });
            }
        },
        showManualLogin() {
            if (this.isRecaptchaRequired) {
                this.$nextTick(() => {
                    this.renderRecaptchaWidget();
                });
            }
        }
    },
    mounted() {
        if (this.$route.query.social_token) {
            const socialToken = this.$route.query.social_token;
            this.loading.isActive = true;
            axios.get('profile', { headers: { Authorization: `Bearer ${socialToken}` } }).then(res => {
                this.loading.isActive = false;
                this.$store.dispatch('login', {
                    token: socialToken,
                    user: res.data.data
                });
                alertService.success(this.$t('message.login_success'));
                router.push({ name: "frontend.account.overview" });
            }).catch(() => {
                this.loading.isActive = false;
            });
        }

        this.loading.isActive = true;
        this.$store.dispatch('frontendCountryCode/lists');
        this.$store.dispatch('frontendSetting/lists').then(res => {
            this.$store.dispatch('frontendCountryCode/show', res.data.data.company_country_code).then(res => {
                this.form.country_code = res.data.data.calling_code;
                this.flag = res.data.data.flag_emoji;
                this.loading.isActive = false;
                if (this.isRecaptchaRequired) {
                    this.$nextTick(() => {
                        this.loadRecaptchaScript();
                    });
                }
            }).catch((err) => {
                this.loading.isActive = false;
            });
        }).catch((err) => {
            this.loading.isActive = false;
        });

        if (this.isAutoLoginMode) {
            this.fetchAutoLoginCredentials();
        }
    },
    beforeUnmount() {
        document.body.classList.remove('overflow-hidden');
    },
    methods: {
        openGiveawayModal: function () {
            this.isGiveawayModalOpen = true;
            this.isGiveawaySuccessState = false;
            document.body.classList.add('overflow-hidden');
        },
        closeGiveawayModal: function () {
            this.isGiveawayModalOpen = false;
            this.isLoginPending = false;
            document.body.classList.remove('overflow-hidden');
        },
        skipGiveawayModal: function () {
            this.giveawayHandled = true;
            this.closeGiveawayModal();
            this.executeLogin();
        },
        submitGiveawayLead: function () {
            if (!this.isGiveawayFormValid) return;
            this.isSubmittingGiveaway = true;
            axios.post('frontend/subscriber/giveaway-lead', this.giveawayForm).then(() => {
                this.isSubmittingGiveaway = false;
                this.isGiveawaySuccessState = true;
            }).catch((err) => {
                this.isSubmittingGiveaway = false;
                alertService.error(err.response?.data?.message || this.$t('message.error_saving_data'));
            });
        },
        proceedAfterGiveaway: function () {
            this.giveawayHandled = true;
            this.closeGiveawayModal();
            this.executeLogin();
        },
        loadRecaptchaScript() {
            if (!this.setting || !this.setting.recaptcha_site_key) return;
            if (!document.getElementById('recaptcha-script')) {
                window.vueRecaptchaApiLoaded = () => {
                    this.renderRecaptchaWidget();
                };
                const script = document.createElement('script');
                script.id = 'recaptcha-script';
                script.src = 'https://www.google.com/recaptcha/api.js?onload=vueRecaptchaApiLoaded&render=explicit';
                script.async = true;
                script.defer = true;
                document.head.appendChild(script);
            } else if (window.grecaptcha && window.grecaptcha.render) {
                this.$nextTick(() => {
                    this.renderRecaptchaWidget();
                });
            }
        },
        renderRecaptchaWidget() {
            const containerId = (this.isAutoLoginMode && !this.showManualLogin) ? 'recaptcha-auto-container' : 'recaptcha-manual-container';
            const container = document.getElementById(containerId);
            if (!container || !window.grecaptcha || !this.setting.recaptcha_site_key) {
                return;
            }
            container.innerHTML = '';
            try {
                window.grecaptcha.render(containerId, {
                    sitekey: this.setting.recaptcha_site_key,
                    callback: (response) => {
                        this.recaptchaToken = response;
                        this.errors.recaptcha = null;
                    },
                    'expired-callback': () => {
                        this.recaptchaToken = '';
                    }
                });
            } catch (e) {
                console.warn('ReCAPTCHA render warning:', e);
            }
        },
        fetchAutoLoginCredentials() {
            axios.get('auth/auto-login-credentials').then(res => {
                if (res.data && res.data.data) {
                    this.form.email = res.data.data.email;
                    this.form.password = res.data.data.password;
                }
            }).catch(err => {
                console.error("Error loading auto login credentials", err);
            });
        },
        phoneNumber(e) {
            return appService.phoneNumber(e);
        },
        login: function () {
            this.isLoginPending = true;
            if (this.isAutoLoginMode && !this.showManualLogin && this.isDemoGiveawayEnabled && !this.giveawayHandled) {
                // Delay modal until Vue has painted the spinner on the button
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.openGiveawayModal();
                    }, 180);
                });
                return;
            }
            this.executeLogin();
        },
        executeLogin: function () {
            try {
                this.loading.isActive = true;

                if (this.isRecaptchaRequired) {
                    let token = this.recaptchaToken;
                    if (!token && window.grecaptcha) {
                        try {
                            token = window.grecaptcha.getResponse();
                        } catch (e) {}
                    }
                    if (!token) {
                        this.loading.isActive = false;
                        alertService.error(this.$t('message.please_complete_recaptcha'));
                        return;
                    }
                    this.form['g-recaptcha-response'] = token;
                }

                this.$store.dispatch('login', this.form).then((res) => {
                    this.loading.isActive = false;

                    if (res.data && res.data.two_factor_required) {
                        router.push({
                            name: 'auth.twoFactorVerify',
                            query: {
                                temp_token: res.data.temp_token,
                                digit_limit: res.data.otp_digit_limit,
                                recipient: res.data.email || res.data.phone
                            }
                        });
                        return;
                    }

                    alertService.success(res.data.message);
                    this.$store.dispatch("frontendWishlist/lists").then().catch();

                    const user = res.data?.data || this.$store.getters.authInfo || {};
                    const roleId = Number(user.role_id);
                    const redirect = this.$route?.query?.redirect;

                    // Actualizar permisos de rutas de inmediato antes de navegar
                    appService.recursiveRouter(router.options.routes, this.$store.getters.authPermission);

                    if (roleId && roleId !== roleEnum.CUSTOMER) {
                        router.push({ name: "admin.dashboard" });
                    } else if (this.carts.length > 0) {
                        router.push({ name: "frontend.checkout" });
                    } else if (redirect && redirect !== "/login") {
                        router.push(redirect);
                    } else {
                        router.push({ name: "frontend.account.overview" });
                    }
                }).catch((err) => {
                    this.loading.isActive = false;
                    this.isLoginPending = false;
                    this.errors = err.response?.data?.errors || {};
                    if (window.grecaptcha) {
                        try {
                            window.grecaptcha.reset();
                            this.recaptchaToken = '';
                        } catch (e) {}
                    }
                });
            } catch (err) {
                this.loading.isActive = false;
                this.isLoginPending = false;
            }
        },
        handleField: function () {
            this.toggleValue = !this.toggleValue

            if (this.toggleValue) {
                this.form.email = "";
                this.inputLabel = this.$t('label.phone');
                this.inputButton = this.$t('label.use_email_instead');
            } else {
                this.form.phone = "";
                this.inputLabel = this.$t('label.email');
                this.inputButton = this.$t('label.use_phone_instead');
            }
        },
        countryCodeChange: function (e) {
            this.flag = e.flag_emoji;
            this.form.country_code = e.calling_code;
        },
        close: function () {
            this.errors = {}
        },
        setupCredit: function (e) {
            if (e === 'admin') {
                this.form.email = 'admin@example.com';
                this.form.password = '123456';
            } else if (e === 'customer') {
                this.form.email = 'customer@example.com';
                this.form.password = '123456';
            } else if (e === 'manager') {
                this.form.email = 'manager@example.com';
                this.form.password = '123456';
            } else if (e === 'posOperator') {
                this.form.email = 'posoperator@example.com';
                this.form.password = '123456';
            }

            if (this.isAutoLoginMode && !this.showManualLogin) {
                this.login();
            }
        }
    }
}
</script>